import { useContext, useMemo } from "react";
import { LocaleContext } from "./LocaleContext";
import { DEFAULT_LOCALE, type Locale } from "./supported";
import { translate } from "./translate";

/**
 * Returns the active locale and a setter.
 *
 * Safe to call outside `<LocaleProvider>` — falls back to `DEFAULT_LOCALE`
 * with a no-op setter so non-public surfaces (admin, portals, embeds) keep
 * working even if they are not yet wrapped.
 */
export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (ctx) return ctx;
  return {
    locale: DEFAULT_LOCALE as Locale,
    setLocale: () => {
      /* no-op outside provider */
    },
  };
}

/**
 * Returns a `t(key, vars?)` translator bound to the active locale.
 * Use this in components instead of importing `translate` directly so the
 * locale dependency is tracked by React.
 */
export function useTranslate() {
  const { locale } = useLocale();
  return useMemo(
    () => (key: string, vars?: Record<string, string | number>) => translate(locale, key, vars),
    [locale],
  );
}
